/**
 * Asset Icon Component
 *
 * Displays icons for different cryptocurrency and asset types
 */

import React from "react";
import { type Asset } from "@/adapters/database";

interface AssetIconProps {
  asset: Pick<Asset, "symbol" | "logoUrl" | "name">;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export const AssetIcon: React.FC<AssetIconProps> = ({ asset, size = "md", className = "" }) => {
  const sizeClasses = {
    sm: "w-6 h-6",
    md: "w-10 h-10",
    lg: "w-16 h-16",
  };

  const iconClasses = {
    sm: "text-xs",
    md: "text-lg",
    lg: "text-2xl",
  };

  const baseClasses = `${sizeClasses[size]} rounded-full flex items-center justify-center mr-3 ${className}`;

  // If asset has a logo URL, try to use it
  if (asset.logoUrl) {
    return (
      <div className={`${baseClasses} bg-sa-surface-hover overflow-hidden`}>
        <img
          src={asset.logoUrl}
          alt={asset.symbol}
          className={`${sizeClasses[size]} object-cover`}
          onError={(e) => {
            // Fallback to symbol-based icon on image load error
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
            target.nextElementSibling?.classList.remove("hidden");
          }}
        />
        <span className={`text-white font-bold ${iconClasses[size]} hidden`}>
          {asset.symbol.charAt(0)}
        </span>
      </div>
    );
  }

  // Symbol-based icons
  switch (asset.symbol.toUpperCase()) {
    case "BTC":
    case "BITCOIN":
      // Official Bitcoin logo from bitcoin.org
      return (
        <div className={`${baseClasses} overflow-hidden p-0`}>
          <svg className={sizeClasses[size]} viewBox="0 0 64 64" fill="none">
            {/* Orange circle background */}
            <path
              d="M63.04 39.74c-4.27 17.14-21.64 27.58-38.78 23.3C7.12 58.76-3.32 41.4.96 24.26 5.23 7.12 22.59-3.32 39.74.96c17.14 4.28 27.58 21.64 23.3 38.78z"
              fill="#F7931A"
            />
            {/* White B symbol with vertical strokes */}
            <path
              d="M46.11 27.44c.64-4.26-2.6-6.55-7.04-8.08l1.44-5.77-3.51-.87-1.4 5.61c-.92-.23-1.87-.45-2.81-.66l1.41-5.65-3.51-.88-1.44 5.77c-.76-.18-1.51-.35-2.24-.53l-4.84-1.21-.93 3.75s2.6.6 2.55.63c1.42.36 1.68 1.3 1.64 2.04l-1.64 6.57c.1.03.22.06.37.12l-.37-.1-2.3 9.2c-.17.44-.61 1.08-1.6.84.03.05-2.56-.64-2.56-.64l-1.74 4.02 4.57 1.14c.85.21 1.68.44 2.5.65l-1.45 5.83 3.51.88 1.44-5.78c.96.26 1.89.5 2.8.73l-1.44 5.75 3.51.87 1.45-5.82c5.99 1.13 10.49.68 12.38-4.74 1.53-4.36-.07-6.88-3.22-8.52 2.29-.53 4.02-2.04 4.48-5.15zm-8.02 11.25c-1.09 4.36-8.43 2-10.81 1.41l1.93-7.73c2.38.6 10.01 1.77 8.88 6.32zm1.09-11.31c-.99 3.97-7.1 1.95-9.08 1.46l1.75-7.01c1.98.49 8.36 1.41 7.33 5.55z"
              fill="#FFFFFF"
            />
          </svg>
        </div>
      );

    case "ETH":
    case "ETHEREUM":
      return (
        <div className={`${baseClasses} bg-indigo-600`}>
          <svg
            className={`text-white ${iconClasses[size]}`}
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2L12.03 8.3L18 12L12 2Z" />
            <path d="M12 2L6 12L12 8.3V2Z" />
            <path d="M12 16.57L18 13.13L12 22V16.57Z" />
            <path d="M12 22L6 13.13L12 16.57V22Z" />
            <path d="M12 15.42L18 12L12 8.3V15.42Z" />
            <path d="M6 12L12 15.42V8.3L6 12Z" />
          </svg>
        </div>
      );

    case "USDC":
    case "USD COIN":
      return (
        <div className={`${baseClasses} bg-primary`}>
          <svg
            className={`text-white ${iconClasses[size]}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
            />
          </svg>
        </div>
      );

    case "USDT":
    case "TETHER":
      return (
        <div className={`${baseClasses} bg-green-600`}>
          <svg
            className={`text-white ${iconClasses[size]}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
            />
          </svg>
        </div>
      );

    case "SOL":
    case "SOLANA":
      return (
        <div className={`${baseClasses} bg-purple-500/10`}>
          <span className={`text-white font-bold ${iconClasses[size]}`}>S</span>
        </div>
      );

    case "ADA":
    case "CARDANO":
      return (
        <div className={`${baseClasses} bg-primary`}>
          <span className={`text-white font-bold ${iconClasses[size]}`}>A</span>
        </div>
      );

    case "DOT":
    case "POLKADOT":
      return (
        <div className={`${baseClasses} bg-pink-600`}>
          <span className={`text-white font-bold ${iconClasses[size]}`}>●</span>
        </div>
      );

    case "GET":
    case "GOLD EAGLE TOKEN":
      return (
        <div className={`${baseClasses} bg-yellow-600`}>
          <span className={`text-white font-bold ${iconClasses[size]}`}>🦅</span>
        </div>
      );

    case "TRX":
    case "TRON":
      return (
        <div className={`${baseClasses} bg-sa-danger/10`}>
          <span className={`text-white font-bold ${iconClasses[size]}`}>T</span>
        </div>
      );

    default:
      return (
        <div className={`${baseClasses} bg-sa-surface-hover`}>
          <span className={`text-white font-bold ${iconClasses[size]}`}>
            {asset.symbol.charAt(0).toUpperCase()}
          </span>
        </div>
      );
  }
};
